import { Button, Input } from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Divider, Form, Segment } from 'semantic-ui-react';
import { OnSaveCallbackParamsType } from '../../../common';
import { dateFormatter, formatStringAsUtcMillisOrNull, toDateObject } from '../../../helpers/dates';
import { useOrganization } from '../../../hooks/useOrganizations';
import { logError } from '../../../rollbar';
import { saveStudent } from '../../studentsAPI';
import { useAllStudents, useSelectedStudent, useStudentEnrollment } from '../../studentsHooks';
// Import components
import { EnrollmentType, StudentType } from '@wonderschool/common-base-types';
import { InlineError, ShowError } from '../../../Components/Messages';
import RoomPicker from '../../../Components/Rooms/RoomPicker';
import { saveContact } from '../../../contacts/contactsAPI';
import {
  EnrollmentFormDataType,
  EnrollmentStatusEnum,
  getScheduledDaysAbbreviated,
  OnSaveDataType,
  ScheduledDayType,
  ScheduleForm,
  TuitionAndFeesType,
  useScheduledDays,
} from '../../../enrollments';
import { useMarketplaceAPI } from '../../../integrations';
import { getRelatedStudentsByContact } from '../../studentsUtils';
import { StudentFormPendingEnrollment } from './StudentFormPendingEnrollment';
import {
  StudentEnrollmentFormPotentialErrors,
  validateStudentEnrollmentForm,
} from './utils/validateStudentEnrollmentForm';

type StudentEnrollmentFormProps = {
  // Should be StudentType, but causing type errors. Typing EnrollmentType and StudentType below seems to work.
  // Parent component StudentDetailProviderApi.tsx passes in student as any.
  student: any;
  onClose: () => void;
};

type OnSaveParamsType = OnSaveCallbackParamsType<ScheduledDayType[] | TuitionAndFeesType>;

export default function StudentEnrollmentForm({ student, onClose }: StudentEnrollmentFormProps): JSX.Element {
  const { t } = useTranslation();
  dayjs.extend(customParseFormat);
  const organization = useOrganization();
  const selectedStudent = useSelectedStudent();
  const { sendEnrollmentStatus } = useMarketplaceAPI();
  const students = useAllStudents();

  const [isSaving, setIsSaving] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [saveData, setSaveData] = useState<OnSaveDataType>({});

  const [errors, setErrors] = useState<StudentEnrollmentFormPotentialErrors>({});
  const [formData, setFormData] = useState<EnrollmentFormDataType>({});

  const scheduledDays = useScheduledDays(student);
  const { automaticallyEnroll, enrollmentStatus, enrollmentDate } = useStudentEnrollment(student);

  useEffect(() => {
    if (!student) return;

    setFormData({
      isEnrolled: enrollmentStatus === EnrollmentStatusEnum.ENROLLED,
      isPending: enrollmentStatus === EnrollmentStatusEnum.PENDING,
      startDate: dateFormatter(enrollmentDate) || '',
      automaticallyEnroll: automaticallyEnroll,
      room: student.rooms?.length ? student.rooms[0] : '',
      scheduledDays: scheduledDays,
    });
    setIsInitializing(false);
  }, [automaticallyEnroll, enrollmentDate, enrollmentStatus, scheduledDays, student]);

  const onSubmit = useCallback(() => {
    const errorsLocal = validateStudentEnrollmentForm(formData);
    if (!isEmpty(errorsLocal)) {
      setErrors((prev) => ({ ...prev, ...errorsLocal }));
    } else if (isEmpty(errors)) {
      setIsSaving(true);
    }
  }, [errors, formData]);

  const shouldSaveToFirestore = useCallback(() => {
    return isSaving && isEmpty(errors) && Boolean(saveData.scheduledDays);
  }, [errors, isSaving, saveData.scheduledDays]);

  const dataToSaveToFirestore = useMemo(() => {
    const enrollment: EnrollmentType = {
      ...(student.enrollment || {}),
      startDate: toDateObject(formData.startDate) ?? undefined,
      automaticallyEnroll: formData.automaticallyEnroll ?? false,
      scheduledDays: saveData.scheduledDays ?? [],
    };

    const data: Partial<StudentType> = {
      id: student.id ?? '',
      enrollmentDate: formData.startDate ? (formatStringAsUtcMillisOrNull(formData.startDate) ?? undefined) : undefined,
      defaultRoom: formData.room,
      rooms: formData.room ? [formData.room] : [],
      enrollment: enrollment,
    };

    // This is legacy and should be deprecated
    data.schedule = getScheduledDaysAbbreviated(data);

    return data;
  }, [formData, saveData, student]);

  const hydrateStudentForSave = useCallback(() => {
    // if room has not changed, then no formatting is needed
    if (selectedStudent.defaultRoom === dataToSaveToFirestore.defaultRoom) {
      return dataToSaveToFirestore;
    }

    const familyContactIds = Object.keys(selectedStudent.family || {});

    // if no family object, then no formatting is needed
    if (!familyContactIds.length) {
      return dataToSaveToFirestore;
    }

    const hydratedStudent = {
      ...dataToSaveToFirestore,
      family: selectedStudent.family,
    };

    for (const contactId of familyContactIds) {
      hydratedStudent.family[contactId] = {
        ...hydratedStudent.family[contactId],
        defaultRoom: dataToSaveToFirestore.defaultRoom,
        rooms: [dataToSaveToFirestore.defaultRoom],
      };
    }

    return hydratedStudent;
  }, [dataToSaveToFirestore, selectedStudent]);

  const updateContactsRoom = useCallback(async () => {
    const familyContactIds = Object.keys(selectedStudent.family || {});
    if (!familyContactIds.length) return;

    // check if room has changed and update contact
    if (selectedStudent.defaultRoom !== dataToSaveToFirestore.defaultRoom) {
      for (const contactId of familyContactIds) {
        // get all related students EXCEPT the one we're editing now.
        const relatedStudents = getRelatedStudentsByContact(
          contactId,
          students?.list?.filter((student) => student.id !== selectedStudent.id)
        );

        const approvedRooms = new Set([dataToSaveToFirestore.defaultRoom]);

        relatedStudents.forEach((relatedStudent) => {
          approvedRooms.add(relatedStudent.rooms[0]);
        });

        const abbreviatedContact = {
          id: contactId,
          defaultRoom: dataToSaveToFirestore.defaultRoom,
          rooms: Array.from(approvedRooms),
        };
        await saveContact(organization.id, abbreviatedContact);
      }
    }
    return true;
  }, [dataToSaveToFirestore, selectedStudent, organization, students]);

  useEffect(() => {
    const saveLocal = async () => {
      try {
        const studentWithUpdatedFamily = hydrateStudentForSave();
        await saveStudent(organization.id, studentWithUpdatedFamily);

        // only performs a write if the room has changed
        await updateContactsRoom();

        if (onClose) onClose();
      } catch (error) {
        logError('Error while saving student', error);
      } finally {
        setIsSaving(false);
        setSaveData({});
      }
    };
    if (shouldSaveToFirestore()) {
      saveLocal();
    }
  }, [
    isSaving,
    organization,
    shouldSaveToFirestore,
    student,
    dataToSaveToFirestore,
    onClose,
    sendEnrollmentStatus,
    hydrateStudentForSave,
    updateContactsRoom,
  ]);

  const onSaveChildForm = useCallback(
    ({ data, errors: errorsLocal }: OnSaveParamsType, fieldName: string) => {
      if (!isSaving) return false;

      if (errorsLocal && !isEmpty(errorsLocal)) {
        setIsSaving(false);
        setSaveData({});
        setErrors((prev) => ({ ...prev, ...errorsLocal }));
      } else {
        setSaveData((prev) => ({ ...prev, [fieldName]: data }));
      }
    },
    [isSaving]
  );

  const onChangeRoom = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, room: value }));
    setErrors((prev) => omit(prev, 'room'));
  }, []);

  const onChangeDate = useCallback(
    (_: any, { name, value, valid }: any) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (valid || isEmpty(value)) {
        setErrors((prev) => omit(prev, name));
      } else {
        setErrors((prev) => ({ ...prev, [name]: t('Invalid Date') }));
      }
    },
    [t]
  );

  const translatedErrorMemo = useMemo(
    () =>
      Object.entries(errors)
        .map(([key, value]) => ({
          [key]: t(value),
        }))
        .reduce((accumulated, current) => Object.assign({}, accumulated, current), {}),
    [errors, t]
  );

  function formatToInputDate(value?: string): string {
    if (!value) return '';
    const m = dayjs(value, 'MM/DD/YYYY', true);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
  }

  function formatFromInputDate(value?: string): { display: string; valid: boolean } {
    if (!value) return { display: '', valid: true };
    const m = dayjs(value, 'YYYY-MM-DD', true);
    return { display: m.isValid() ? m.format('MM/DD/YYYY') : value, valid: m.isValid() };
  }

  return (
    <>
      <Segment basic textAlign="left">
        <ShowError errors={translatedErrorMemo} data-testid="student-enrollment-form-errors" />
        <Form onSubmit={onSubmit} loading={isSaving || isInitializing} noValidate data-testid="student-enrollment-form">
          <Form.Field error={!!errors.room} data-testid="room-field-error">
            <RoomPicker
              error={!!errors.room}
              id="room"
              name="room"
              label={t('Room')}
              placeholder={t('Select a room')}
              value={formData?.room ?? ''}
              selection
              search
              required
              onChange={onChangeRoom}
              data-testid="room-picker"
              extraOptionsClasses="z-[1000]"
            />
            {errors.room && <InlineError text={t(errors.room)} data-testid="room-error-msg" />}
          </Form.Field>

          <Form.Field error={!!errors.startDate} data-testid="startDate-field-error">
            <Input
              id="startDate"
              name="startDate"
              label={t('enrollments.enrollmentInfoStartDateLabel')}
              inputType="date"
              value={formatToInputDate(formData?.startDate)}
              onChange={(event) => {
                const { display, valid } = formatFromInputDate((event.target as HTMLInputElement).value);
                onChangeDate(null, { name: 'startDate', value: display, valid });
              }}
              placeholder={t('Select an enrollment date')}
              error={errors.startDate && t(errors.startDate)}
              required
              data-testid="startDate"
            />
          </Form.Field>

          {enrollmentStatus === EnrollmentStatusEnum.PENDING && (
            <StudentFormPendingEnrollment
              automaticallyEnroll={Boolean(formData?.automaticallyEnroll)}
              setFormData={setFormData}
              studentId={student.id}
            />
          )}
          <Form.Field>
            <Divider />
          </Form.Field>
          <Form.Field>
            <ScheduleForm
              scheduledDays={formData?.scheduledDays ?? []}
              isSaving={isSaving && !saveData.scheduledDays}
              onSave={(d) => onSaveChildForm(d, 'scheduledDays')}
              onClearErrors={() => setErrors((prev) => omit(prev, 'noScheduleSelected'))}
            />
          </Form.Field>

          <Form.Group>
            <div className="mt-4 grid w-full grid-cols-1 items-center gap-3 sm:grid-cols-2">
              <Button
                label={t('Save')}
                primary
                onClick={onSubmit}
                disabled={isSaving || !isEmpty(errors)}
                loading={isSaving}
                data-testid="enrollment-edit-form-save-btn"
                extraClasses="w-full order-1 sm:order-2"
              />
              <Button
                label={t('common.cancel')}
                onClick={(e) => {
                  if (e) e.preventDefault();
                  if (onClose) onClose();
                }}
                data-testid="enrollment-edit-form-cancel-btn"
                extraClasses="w-full order-2 sm:order-1"
              />
            </div>
          </Form.Group>
        </Form>
      </Segment>
    </>
  );
}
